import React, { useMemo, useState } from "react";

import { Box } from "@chakra-ui/react";
import {
  IBusinessDetailDocument,
  IBusinessDetails,
} from "api/business-user-management/types";
import { useApproveOrRejectBusinessSection } from "hooks/useApproveOrRejectBusinessSection";
import { getBusinessDocumentTitle } from "utils";
import { REGISTERED_BUSINESS_COMPANY_TYPE, START_INDEX } from "utils/constants";

import { ImageViewer } from "ui/image-viewer";

import { ApproveBusinessSectionModal } from "./ApproveBusinessSectionModal";
import { BusinessContentItem } from "./BusinessContentItem";
import { BusinessDocumentView } from "./BusinessDocumentView";
import { BusinessSectionAction } from "./BusinessSectionAction";
import { RejectBusinessSectionModal } from "./RejectBusinessSectionModal";
import {
  IBusinessContentItem,
  IBusinessDocumentProps,
  IBusinessDocumentView,
} from "./types";

export const addressDocumentLabel: Record<string, string> = {
  "utility-bill": "Utility Bill (Electricity or Waste Bill)",
  "bank-statement": "Bank Statement from a Commercial Bank",
};

export const unRegisteredBusinessDirectorDocsLabel: Record<string, string> = {
  "national-id": "National ID Card or NIN Slip",
  "international-passport": "International Passport",
  "driver's-license": "Driver's License",
};

const proofOfAddressDocNames = ["utility-bill", "bank-statement"];
const validDocNamesUnregisteredBusiness = [...proofOfAddressDocNames];
const validDocNamesRegisteredBusiness = [
  ...proofOfAddressDocNames,
  "cac",
  "particulars-of-directors",
];

const findAddressDocsLabel = (documents: IBusinessDetailDocument[]) => {
  for (const doc of documents) {
    if (addressDocumentLabel[doc.name]) {
      return addressDocumentLabel[doc.name];
    }
  }
  return "-";
};

const filterMostRecentValidBusinessDocument = (
  businessDocs: IBusinessDetailDocument[],
  business: IBusinessDetails
): IBusinessDetailDocument[] => {
  let latestBankStatement = null;
  let latestUtilityBill = null;
  const validBusinessDocumentNames =
    business?.business?.companyData?.companyType ===
    REGISTERED_BUSINESS_COMPANY_TYPE
      ? validDocNamesRegisteredBusiness
      : validDocNamesUnregisteredBusiness;

  businessDocs.forEach((obj) => {
    if (obj.name === "bank-statement") {
      latestBankStatement = obj;
    } else if (obj.name === "utility-bill") {
      latestUtilityBill = obj;
    }
  });

  const result = businessDocs.filter((obj) => {
    if (obj.name === "bank-statement" || obj.name === "utility-bill") {
      return false; // Remove these temporarily
    }
    return true; // Keep everything else
  });

  if (
    latestUtilityBill &&
    (!latestBankStatement ||
      businessDocs.indexOf(latestUtilityBill) >
        businessDocs.indexOf(latestBankStatement))
  ) {
    result.push(latestUtilityBill); // Add utility bill if it's the most recent
  } else if (latestBankStatement) {
    result.push(latestBankStatement); // Add bank statement if it's the most recent
  }
  return result?.filter(({ name }) =>
    validBusinessDocumentNames.includes(name)
  );
};

export const BusinessDocuments = (props: IBusinessDocumentProps) => {
  const {
    business,
    status,
    isContentWrapperBelow745,
    isContentWrapperBelow600,
    isContentWrapperBelow450,
    title,
  } = props;

  const [showRejectionReasonModal, setShowRejectionReasonModal] =
    useState(false);
  const [showApprovalSectionModal, setShowApprovalSectionModal] =
    useState(false);
  const [showBusinessDocumentImages, setShowBusinessDocumentImages] =
    useState(false);
  const [businessDocumentIndex, setBusinessDocumentIndex] =
    useState(START_INDEX);

  const handleToggleApproveOrRejectModal = () => {
    setShowApprovalSectionModal(false);
    setShowRejectionReasonModal(false);
  };

  const {
    approveBusinessSection: approveBusinessDocuments,
    rejectBusinessSection: rejectBusinessDocuments,
    isApprovingOrRejecting,
  } = useApproveOrRejectBusinessSection({
    businessId: business?.business?.id,
    businessSectionTitle: title,
    handleToggleApproveOrRejectModal,
  });
  const handleBusinessDocumentImageIndexChange = (imageIndex: number) => {
    setBusinessDocumentIndex(imageIndex);
  };

  const handleOpenBusinessDocumentImages = (
    currentBusinessDocIndex: number
  ) => {
    handleBusinessDocumentImageIndexChange(currentBusinessDocIndex);
    setShowBusinessDocumentImages(!showBusinessDocumentImages);
  };

  const handleCloseBusinessDocumentImages = () => {
    setShowBusinessDocumentImages(false);
  };

  const handleToggleRejectionReasonModal = () => {
    setShowRejectionReasonModal(!showRejectionReasonModal);
  };

  const handleToggleApprovalSectionModal = () => {
    setShowApprovalSectionModal(!showApprovalSectionModal);
  };

  const businessDocumentsList: IBusinessContentItem[] = useMemo(
    () => [
      ...(business?.business?.companyData?.companyType ===
      REGISTERED_BUSINESS_COMPANY_TYPE
        ? [
            {
              title: "Country",
              content: business?.business?.companyData?.country || "-",
            },
          ]
        : []),
      {
        title: "Address",
        content: business?.business?.companyData?.address || "-",
      },
      {
        title:
          business?.business?.companyData?.companyType ===
          REGISTERED_BUSINESS_COMPANY_TYPE
            ? "Proof of Address Type"
            : "Document Type",
        content: findAddressDocsLabel(
          business?.business?.companyData?.businessDocuments || []
        ),
      },
      {
        title: "State",
        content: business?.business?.companyData?.state || "-",
      },
    ],
    [isContentWrapperBelow745, isContentWrapperBelow600]
  );

  const businessDocumentsImage: IBusinessDocumentView[] = useMemo(() => {
    if (!business?.business?.companyData?.businessDocuments) return [];

    return filterMostRecentValidBusinessDocument(
      business?.business?.companyData.businessDocuments,
      business
    )?.map((doc) => {
      return {
        title: getBusinessDocumentTitle(doc.name),
        imageUrl: doc.fileUrl,
      };
    });
  }, [business?.business?.companyData]);

  const isBusinessDocumentsCtaDisabled = business?.business?.companyData
    ?.businessDocumentStatus
    ? business?.business?.companyData?.businessDocumentStatus?.status !==
      "pending"
    : !!business?.business?.companyData?.businessDocumentStatus;

  return (
    <Box>
      <Box
        css={{
          display: "flex",
          flexWrap: "wrap",
          columnGap: "32px",
          rowGap: "16px",
        }}
      >
        {businessDocumentsList.map(({ title, content }, index) => (
          <BusinessContentItem
            key={index}
            title={title}
            content={content}
            isContentWrapperBelow745={isContentWrapperBelow745}
            isContentWrapperBelow600={isContentWrapperBelow600}
          />
        ))}
      </Box>
      <Box
        className="flex flex-wrap mt-4"
        css={{
          columnGap: "32px",
          rowGap: "16px",
        }}
      >
        {businessDocumentsImage.map(({ title, imageUrl }, index) => {
          return (
            <BusinessDocumentView
              key={index}
              title={title}
              imageUrl={imageUrl}
              isContentWrapperBelow745={isContentWrapperBelow745}
              isContentWrapperBelow600={isContentWrapperBelow600}
              handleOpenBusinessDocumentImages={() =>
                handleOpenBusinessDocumentImages(index)
              }
            />
          );
        })}
      </Box>
      {status === "review" && (
        <BusinessSectionAction
          isRejectSectionDisabled={isBusinessDocumentsCtaDisabled}
          isApproveSectionDisabled={isBusinessDocumentsCtaDisabled}
          handleAddRejectionReason={handleToggleRejectionReasonModal}
          handleApproveSection={handleToggleApprovalSectionModal}
          isContentWrapperBelow450={isContentWrapperBelow450}
        />
      )}
      <ApproveBusinessSectionModal
        isOpen={showApprovalSectionModal}
        setIsOpen={setShowApprovalSectionModal}
        handleToggleApprovalSectionModal={handleToggleApprovalSectionModal}
        handleApproveBusinessSection={approveBusinessDocuments}
        isLoading={isApprovingOrRejecting}
      />
      <RejectBusinessSectionModal
        isOpen={showRejectionReasonModal}
        setIsOpen={setShowRejectionReasonModal}
        handleToggleRejectSectionModal={handleToggleRejectionReasonModal}
        handleRejectBusinessSection={rejectBusinessDocuments}
        isLoading={isApprovingOrRejecting}
      />
      <ImageViewer
        imageDataList={businessDocumentsImage}
        isVisible={showBusinessDocumentImages}
        imageIndex={businessDocumentIndex}
        handleCloseImageViewer={handleCloseBusinessDocumentImages}
        handleImageIndexChange={handleBusinessDocumentImageIndexChange}
        uploaderName={`${business?.business.firstName} ${business?.business?.lastName}`}
      />
    </Box>
  );
};
